const MP_RUNTIMES = [
  {
    moduleUrl: '/vendor/mediapipe/vision_bundle.mjs',
    wasmRoot: '/vendor/mediapipe/wasm'
  },
  {
    moduleUrl: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/vision_bundle.mjs',
    wasmRoot: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm'
  },
  {
    moduleUrl: 'https://unpkg.com/@mediapipe/tasks-vision@0.10.34/vision_bundle.mjs',
    wasmRoot: 'https://unpkg.com/@mediapipe/tasks-vision@0.10.34/wasm'
  }
];

const MP_MODELS = [
  '/vendor/mediapipe/models/hand_landmarker.task',
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task'
];

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function fingerExtended(landmarks, tipIndex, pipIndex, mcpIndex) {
  const tip = landmarks[tipIndex];
  const pip = landmarks[pipIndex];
  const mcp = landmarks[mcpIndex];
  return tip.y < pip.y && pip.y < mcp.y;
}

function isOpenPalm(landmarks) {
  return (
    fingerExtended(landmarks, 8, 6, 5) &&
    fingerExtended(landmarks, 12, 10, 9) &&
    fingerExtended(landmarks, 16, 14, 13) &&
    fingerExtended(landmarks, 20, 18, 17)
  );
}

function isFist(landmarks) {
  const wrist = landmarks[0];
  const tips = [8, 12, 16, 20].map((idx) => landmarks[idx]);
  const mcps = [5, 9, 13, 17].map((idx) => landmarks[idx]);
  return tips.every((tip, index) => distance(tip, wrist) < distance(mcps[index], wrist) * 1.15);
}

export class GestureController {
  constructor({ videoEl, onGesture, onStatus }) {
    this.videoEl = videoEl;
    this.onGesture = onGesture;
    this.onStatus = onStatus;

    this.stream = null;
    this.handLandmarker = null;
    this.running = false;
    this.frameRequest = null;
    this.wasPinching = false;

    this.stableGestureCandidate = null;
    this.stableCount = 0;
    this.emittedGesture = null;
    this.lastPalmCenter = null;
  }

  async start() {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('当前浏览器不支持摄像头访问。');
      }

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      this.videoEl.srcObject = this.stream;
      await this.videoEl.play();

      this.handLandmarker = await this.createHandLandmarker();

      this.running = true;
      this.onStatus({
        type: 'GESTURE_READY',
        message: '手势已启用'
      });

      this.loop();
    } catch (error) {
      this.onStatus({
        type: 'GESTURE_ERROR',
        message: `手势不可用：${error.message || '摄像头或模型初始化失败'}`
      });
      this.stop();
    }
  }

  async createHandLandmarker() {
    const errors = [];

    for (const runtime of MP_RUNTIMES) {
      try {
        const visionModule = await import(/* @vite-ignore */ runtime.moduleUrl);
        const { FilesetResolver, HandLandmarker } = visionModule;
        const fileset = await FilesetResolver.forVisionTasks(runtime.wasmRoot);

        for (const modelUrl of MP_MODELS) {
          try {
            const landmarker = await HandLandmarker.createFromOptions(fileset, {
              baseOptions: {
                modelAssetPath: modelUrl
              },
              runningMode: 'VIDEO',
              numHands: 1,
              minHandDetectionConfidence: 0.55,
              minHandPresenceConfidence: 0.55,
              minTrackingConfidence: 0.55
            });
            return landmarker;
          } catch (error) {
            errors.push(
              `模型加载失败（${modelUrl}）：${error instanceof Error ? error.message : String(error)}`
            );
          }
        }
      } catch (error) {
        errors.push(
          `运行时加载失败（${runtime.moduleUrl}）：${error instanceof Error ? error.message : String(error)}`
        );
      }
    }

    throw new Error(errors.join(' | '));
  }

  loop() {
    if (!this.running || !this.handLandmarker) {
      return;
    }

    const now = performance.now();
    const results = this.handLandmarker.detectForVideo(this.videoEl, now);
    const landmarks = results?.landmarks?.[0];

    if (!landmarks) {
      this.lastPalmCenter = null;
      this.stableGestureCandidate = null;
      this.stableCount = 0;
      this.emittedGesture = null;
      this.wasPinching = false;
      this.frameRequest = requestAnimationFrame(() => this.loop());
      return;
    }

    this.processStaticGestures(landmarks);
    this.processRotation(landmarks);
    this.processPinch(landmarks);

    this.frameRequest = requestAnimationFrame(() => this.loop());
  }

  processStaticGestures(landmarks) {
    let candidate = null;
    if (isFist(landmarks)) {
      candidate = 'FIST';
    } else if (isOpenPalm(landmarks)) {
      candidate = 'OPEN_PALM';
    }

    if (!candidate) {
      this.stableGestureCandidate = null;
      this.stableCount = 0;
      return;
    }

    if (candidate === this.stableGestureCandidate) {
      this.stableCount += 1;
    } else {
      this.stableGestureCandidate = candidate;
      this.stableCount = 1;
    }

    if (this.stableCount >= 3 && this.emittedGesture !== candidate) {
      this.emittedGesture = candidate;
      this.onGesture({ type: candidate });
    }
  }

  processRotation(landmarks) {
    if (!isOpenPalm(landmarks)) {
      this.lastPalmCenter = null;
      return;
    }

    const palmCenter = {
      x: (landmarks[0].x + landmarks[5].x + landmarks[17].x) / 3,
      y: (landmarks[0].y + landmarks[5].y + landmarks[17].y) / 3
    };

    if (this.lastPalmCenter) {
      const dx = palmCenter.x - this.lastPalmCenter.x;
      const dy = palmCenter.y - this.lastPalmCenter.y;
      if (Math.abs(dx) > 0.003 || Math.abs(dy) > 0.003) {
        this.onGesture({
          type: 'ROTATE',
          deltaX: dx,
          deltaY: dy
        });
      }
    }

    this.lastPalmCenter = palmCenter;
  }

  processPinch(landmarks) {
    const thumbTip = landmarks[4];
    const indexTip = landmarks[8];
    const pinchDistance = Math.hypot(thumbTip.x - indexTip.x, thumbTip.y - indexTip.y);
    const isPinching = pinchDistance < 0.045;

    const payload = {
      x: indexTip.x,
      y: indexTip.y
    };

    if (isPinching && !this.wasPinching) {
      this.onGesture({ type: 'PINCH_START', ...payload });
    } else if (isPinching && this.wasPinching) {
      this.onGesture({ type: 'PINCH_MOVE', ...payload });
    } else if (!isPinching && this.wasPinching) {
      this.onGesture({ type: 'PINCH_END', ...payload });
    }

    this.wasPinching = isPinching;
  }

  stop() {
    this.running = false;
    if (this.frameRequest) {
      cancelAnimationFrame(this.frameRequest);
      this.frameRequest = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.videoEl) {
      this.videoEl.srcObject = null;
    }
  }
}
